import { Html5Qrcode } from "html5-qrcode";
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next";
import API from "../service/api";
import { useNavigate } from "react-router";
import { CustomSelect } from "./CustomSelect";
import { Data } from "../service/global";
import { Tooltip } from "react-tooltip";
import { MDBBtn, MDBIcon } from "mdb-react-ui-kit";
import { MachineConnectionButton } from "./MachineConnectionButton";
import { MachinePowerButton } from "./MachinePowerButton";
import { NavLink } from "react-router-dom";


interface QRCameraObject {
    deviceId: string;
    label: string;
    sz: number;
}

export interface QRScannerProps {
    currentGroupId: number;
    onClose: () => void;
}

export const QRScanner = ({currentGroupId, onClose}: QRScannerProps) => {
    const [scanner, setScanner] = useState<Html5Qrcode|null>(null);
    const [status, setStatus] = useState(0); //0 - nothing, 1 - no data, 2 - data but not valid SN, 3 - valid SN
    const [cameraList, setCameraList] = useState<QRCameraObject[]|null>(null);
    const [selectedCameraId, setSelectedCameraId] = useState<string|null>(null);
    const [sn, setSN] = useState('');
    const [error, setError] = useState(null);
    const qrarea = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();

    const close = useCallback(async () => {
        if (!scanner) return;
        try {
            await scanner.stop()
            scanner.clear();
        }catch(err){
            console.error(err);
        }
        onClose();
    }, [scanner]);

    const selectCamera = useCallback((id: string) => {
        setSelectedCameraId(id);
        localStorage.setItem('qrcamera', id);
    }, []);

    const onDecodedText = useCallback((text: string) => {
        if (!scanner) return;

        try {
            if (textIsSN(text)){
                scanner.pause(true);
                try {
                    navigator.vibrate(200);
                }catch(err){};
                setSN(text);
                setStatus(3);
            }else{
                setStatus(2);
            }
        }catch(err: any){ setError(err.message); }
        
    }, [scanner]);

    const textIsSN = useCallback((text: string) => {
        return /^[0-9a-fA-F]+$/.test(text) && text.length >= 8;
    }, []);

    const start = useCallback(() => {
        if (scanner && selectedCameraId) {
            try {
                scanner.start(
                    selectedCameraId,
                    { fps: 20, qrbox: 250 },
                    onDecodedText,
                    _ => setStatus(1)
                );
            }catch(err: any){
                setError(err.message);
            }
        }
    }, [scanner, selectedCameraId]);

    useEffect(() => {
        const handler = () => close();
        window.addEventListener('popstate', handler);
        return () => {
            window.removeEventListener('popstate', handler);
        }
    }, [scanner]);
    
    useEffect(() => {
        let html5Qrcode = new Html5Qrcode(
            "reader",
        );
        setScanner(html5Qrcode);

        if (window.navigator){

            navigator.mediaDevices.getUserMedia({ video: true })
                .then(() => {
                    navigator.mediaDevices.enumerateDevices()
                        .then(devices => {
                            const cameras = devices.filter(d => d.kind == 'videoinput').map(d => {
                                let sz = 0;
                                return {deviceId: d.deviceId, label: d.label, sz};
                            }).filter(d => !!d);
                            cameras.sort((a,b) => {
                                return b.sz - a.sz;
                            });
                            setCameraList(cameras);
                            const localCamera = localStorage.getItem('qrcamera');
                            if (localCamera && cameras.find(c => c.deviceId == localCamera)) selectCamera(localCamera);
                            else if (cameras.length > 0) selectCamera(cameras[0].deviceId);
                        })
                })
                .catch(err => setError(err.message));
        }
        
        return () => {
            close();
        }
    }, []);

    useEffect(() => {
        if (selectedCameraId && scanner){
            if ([2,3].includes(scanner.getState())){
                scanner.stop()
                    .then(() => {
                        start();
                    })
            }else start();
        }
    }, [selectedCameraId, scanner]);
    

    return (
        <div className="d-flex flex-column position-fixed w-100 h-100 bg-dark align-items-center overflow-hidden" style={{zIndex: 1001}}>
            <nav className="navbar navbar-expand-lg navbar-light bg-light p-0 w-100" style={{zIndex: 1003}}>
                <div className="container-fluid justify-content-between px-2 py-1">
                    <span
                        className="nav-link d-flex align-items-center user-select-none cursor-pointer position-absolute ps-2"
                        onClick={() => { close() }}
                        style={{ cursor: "pointer"}}
                    >
                        <div className="d-flex align-items-center pe-1">
                            <i
                                className="fas fa-arrow-left nav-link"
                                style={{fontSize: "1.8rem" }}
                            />
                        </div>
                    </span>

                    <span className="d-flex flex-fill justify-content-end align-items-center me-1">
                        <CustomSelect width={250}
                            items={(cameraList||[]).map(c => ({
                                val: c.deviceId, title: c.label, icon: 'fas fa-camera'
                            }))}
                            selected={selectedCameraId}
                            onSelect={id => selectCamera(String(id))}
                        />
                    </span>
                </div>
            </nav>

            
            {!error && <>
                <div id="reader" ref={qrarea} className="bg-light d-flex flex-fill h-100 w-100" style={{zIndex: 1002}}></div>
                {cameraList==null && 
                    <div className="d-flex flex-column w-100 h-100 justify-content-center align-items-center position-absolute" style={{zIndex: 1003, marginTop: 80, marginBottom: 80}}>
                        <div className="spinner-border mt-2 position-absolute" role="status" style={{width: '5rem', height: '5rem'}}>
                            <span className="visually-hidden">{t('loading...')}</span>
                        </div>
                    </div>
                }
                {status === 1 &&
                    <span className="badge badge-primary position-fixed" style={{bottom: 5, zIndex: 1003}}>Scan the QR code of a machine!</span>
                }
                {status === 2 &&
                    <span className="badge badge-danger position-fixed" style={{bottom: 5, zIndex: 1003}}>Invalid QR code!</span>
                }
            </>}
            {error && <>
                <div className="bg-dark w-100 d-flex flex-column flex-fill align-items-center justify-content-start mt-5">
                    <i className="far fa-circle-xmark fa-8x text-danger"/>
                    <h1 className="text-danger mt-1">{t('issue_occured')}</h1>
                    <h6 className="text-center mx-2 text-muted">{error}</h6>

                </div>
            </>}

            <SNAnalyzer
                currentGroupId={currentGroupId}
                sn={sn}
                onClose={close}
                onCancel={() => {
                    setSN('');
                    setStatus(1);
                    if (scanner) scanner.resume();
                }}
            />
        </div>
    )
}

const SNAnalyzer = ({sn, currentGroupId, onCancel, onClose}: {
    sn: string,
    currentGroupId: number,
    onCancel: () => void,
    onClose: () => void
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [commandError, setCommandError] = useState(null);
    const [result, setResult] = useState<any|null>(null);
    const { t } = useTranslation();
    

    useEffect(() => {
        setError(null);
        setCommandError(null);
        setResult(null);
        setLoading(true);
        if (sn){
            API.POST('/api/groups/qr', {sn, currentGroupId})
                .then(r => {
                    setResult(r);
                })
                .catch(err => {
                    setError(err.message);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [sn, currentGroupId]);


    const openDoor = useCallback(() => {
        setCommandError(null);

        API.POST(`/api/groups/${result.operator.group?.id}/${result.operator.location?.id}/${result.machine?.id}/action`, {id: "opendoor"})
            .then(() => {
            })
            .catch(err => {
                setCommandError(err.message);
            });
    }, [result]);

    return (
        <div className="d-flex flex-column align-items-center pt-3 w-100 h-75 bg-light position-fixed overflow-hidden" style={{maxWidth: 500, bottom: 0, zIndex: 1004,
            transform: `` + (!sn ? 'translateY(150px)' :''), opacity: !sn ? 0 : 1, transition: 'transform .4s ease-out, opacity .2s ease-out',
            borderTopLeftRadius: '0.5rem', borderTopRightRadius: '0.5rem'}}>

            <h5 className="text-muted">{sn}</h5>
            {loading &&
                <div className="spinner-border mt-2" role="status" style={{width: '5rem', height: '5rem'}}>
                    <span className="visually-hidden">{t('loading...')}</span>
                </div>
            }
            {error && <>
                <div className="d-flex flex-column align-items-center">
                    <i className="far fa-circle-xmark fa-8x text-danger"/>
                    <h1 className="text-danger mt-1">{error}</h1>
                </div>
            </>}
            {result && <>
                <div className="d-flex flex-row w-100 overflow-hidden">
                    <div className="d-flex flex-row flex-fill overflow-hidden pe-2">
                        <span className="w-100 mb-1 text-primary text-center fw-bold overflow-hidden whitespace-nowrap">{result?.machine.name}</span>
                    </div>
                    <div className="d-flex flex-row position-absolute justify-content-center align-items-center" style={{right: 10}}>
                        <MachineConnectionButton machine={result?.machine} tooltipId="qr-connection-status"/>
                        <MachinePowerButton machine={result?.machine} tooltipId="qr-power-status"/>
                    </div>
                </div>
                
                <span><i className="fas fa-map-marker-alt me-1"/>{result?.operator?.location?.Name}</span>
                { result.operator &&
                    <NavLink
                        className="w-75 mt-3"
                        to={`/group/${result.operator.group?.id}/locations/${result.operator.location?.id}/machines/${result?.machine?.id}`}
                        onClick={onClose}
                    >
                        <button className="btn btn-primary w-100"><i className="fas fa-desktop me-1"/>Operator</button>
                    </NavLink>
                    
                }
                { result.admin &&
                    <NavLink
                        className="w-75 mt-3"
                        to={`/profile/machines?mid=${result?.machine.id}`}
                        onClick={onClose}
                    >
                        <button className="btn btn-primary w-100"><i className="fas fa-user-gear me-1"/>Administrator</button>
                    </NavLink>
                }
                { result.openDoor &&
                    <button className="btn btn-primary mt-3 w-75"
                        onClick={openDoor}><i className="fas fa-door-open me-1"/>{t('open_door')}</button>
                }

                { commandError && <div className="alert alert-danger alert-dismissible fade show mt-2" role="alert">
                    { commandError }
                </div> }
            </>}

            <div className="d-flex flex-fill"></div>

            <button className="btn btn-danger w-75 mb-2" onClick={() => onCancel()}>{t('cancel')}</button>

        </div>
        
    )
}